import { resetPasswordSchema } from '@school-erp/validation';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, ApiError } from '../../api/http';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { AuthLayout } from './AuthLayout';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    // Same schema the API enforces, so the user gets immediate feedback.
    const parsed = resetPasswordSchema.safeParse({ token, password });
    if (!parsed.success) {
      const passwordIssue = parsed.error.issues.find((issue) => issue.path[0] === 'password');
      setFieldError(passwordIssue?.message ?? t('auth.resetInvalid'));
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      await api('/auth/reset-password', { method: 'POST', body: parsed.data, anonymous: true });
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === 'INVALID_OR_EXPIRED_TOKEN'
          ? t('auth.resetInvalid')
          : t('auth.genericError'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title={t('auth.resetTitle')}>
      {done ? (
        <p role="status" className="text-sm text-slate-700">
          {t('auth.resetDone')}
        </p>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <TextField
            label={t('auth.newPassword')}
            type="password"
            name="password"
            autoComplete="new-password"
            hint={t('auth.passwordHint')}
            error={fieldError}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {t('auth.resetSubmit')}
          </button>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to="/login" className="text-brand-700 underline">
          {t('auth.backToSignIn')}
        </Link>
      </p>
    </AuthLayout>
  );
}
